import React from 'react';
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { OnboardingStatus } from '../../services/onboardingService';
import { ActivationStep, StepKey, activationProgress, buildActivationSteps } from './steps';

export interface ActivationChecklistProps {
  status: OnboardingStatus | null;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** Replace the built-in link button for a step (e.g. create the key in place). */
  overrides?: Partial<Record<StepKey, React.ReactNode>>;
  /** Show the green "setup complete" line; the dashboard hides it once done. */
  celebrate?: boolean;
  title?: React.ReactNode;
}

/** The shared onboarding checklist: progress, one next step, a button for every step. */
export const ActivationChecklist: React.FC<ActivationChecklistProps> = ({
  status,
  loading,
  error,
  onRetry,
  overrides,
  celebrate = true,
  title = 'Set up in four steps',
}) => {
  const steps = buildActivationSteps(status);
  const { done, total, percent } = activationProgress(steps);
  const complete = done === total;

  if (!status && loading && !error) {
    return (
      <Box data-testid="activation-loading">
        <Typography variant="subtitle1" component="h3" gutterBottom>{title}</Typography>
        <LinearProgress aria-label="Loading setup progress" sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">Checking your account…</Typography>
      </Box>
    );
  }

  const action = (step: ActivationStep) => {
    const override = overrides?.[step.key];
    if (override) return override;
    return (
      <Button
        component={RouterLink}
        to={step.to}
        size="small"
        variant={step.state === 'next' ? 'contained' : 'outlined'}
      >
        {step.actionLabel}
      </Button>
    );
  };

  return (
    <Box>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={onRetry ? <Button color="inherit" size="small" onClick={onRetry}>Retry</Button> : undefined}
        >
          {error}
        </Alert>
      )}

      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
          <Typography variant="subtitle1" component="h3">{title}</Typography>
          <span className="gt-ledger-progress" data-testid="activation-progress">
            <strong className="num">{done}</strong> of {total} done
          </span>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={percent}
          aria-label={`Setup progress: ${done} of ${total} steps complete`}
          sx={{ height: 6, borderRadius: 3 }}
        />
        {!complete && (
          <Typography variant="body2" color="text.secondary">
            About five minutes. Your test key is free and no rate or rule data changes.
          </Typography>
        )}
      </Stack>

      {complete && celebrate && (
        <Alert severity="success" sx={{ mb: 2 }} data-testid="activation-complete">
          Setup complete. Your first calculation is working — the latest activity is on your dashboard.
        </Alert>
      )}

      <ol className="gt-ledger" aria-label="Setup checklist">
        {steps.map((step) => (
          <li key={step.key} className="gt-ledger-row" data-state={step.state} data-step={step.key}>
            <span className="gt-ledger-mark" aria-hidden="true" />
            <div>
              <div className="gt-ledger-title">
                {step.title}
                <span className="visually-hidden">
                  {step.done ? ' (done)' : step.state === 'next' ? ' (next step)' : ' (not started)'}
                </span>
              </div>
              <div className="gt-ledger-note">{step.note}</div>
            </div>
            <div>
              {step.done ? (
                <Typography variant="body2" color="success.main">Done</Typography>
              ) : (
                action(step)
              )}
            </div>
          </li>
        ))}
      </ol>

      {loading && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress aria-label="Refreshing setup progress" />
        </Box>
      )}
    </Box>
  );
};

export default ActivationChecklist;
